import { Link } from "react-router-dom";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { PEOPLE } from "@/data/site";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { SSButton } from "@/components/site/SSButton";
import { ContextualCTA } from "@/components/site/ContextualCTA";

const PATHS = { suhail: "/suhail-abbas-zaidi", vibhor: "/vibhor-nautiyal" };

export default function PersonProfile({ personKey }) {
  const person = PEOPLE.find((p) => p.key === personKey);
  if (!person) return null;
  return <ProfileContent key={person.key} person={person} />;
}

function ProfileContent({ person }) {
  const path = PATHS[person.key];
  const description = person.bio?.[0] || `${person.name} is part of Selling Sunsets, a property-intelligence and buyer-advisory practice for Uttarakhand.`;

  useSeo({
    title: `${person.name} | ${person.role} — Selling Sunsets`,
    description,
    path,
    image: person.photo,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": `https://sellingsunsets.co.in${path}#person`,
      name: person.name,
      url: `https://sellingsunsets.co.in${path}`,
      image: `https://sellingsunsets.co.in${person.photo}`,
      jobTitle: person.role,
      description,
      homeLocation: { "@type": "Place", name: "Dehradun, Uttarakhand, India" },
      worksFor: {
        "@type": "Organization",
        "@id": "https://sellingsunsets.co.in/#organization",
        name: "Selling Sunsets",
        url: "https://sellingsunsets.co.in",
      },
      knowsAbout: ["Property intelligence", "Buyer advisory", "Uttarakhand real estate", "Dehradun property"],
      ...(person.linkedin ? { sameAs: [person.linkedin] } : {}),
    },
  });

  return (
    <>
      <PageHeader
        eyebrow={person.headline}
        marker={person.key === "suhail" ? "SS·SAZ" : "SS·VN"}
        title={person.name}
        intro={description}
        breadcrumbs={[{ label: "About", to: "/about" }, { label: person.name }]}
      />

      <section className="ss-container pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-[minmax(280px,420px)_1fr] gap-10 lg:gap-20 items-start">
          <Reveal className="overflow-hidden aspect-[4/5] bg-[#181A1B]">
            <img src={person.photo} alt={person.photoAlt || person.name} className="h-full w-full object-cover object-top" />
          </Reveal>

          <div className="max-w-2xl">
            <p className="ss-overline text-[#8C6A53] mb-4">{person.role} · Selling Sunsets</p>
            <h2 className="font-display text-3xl sm:text-4xl font-light leading-tight text-[#1A1C1E]">
              Property intelligence for better-informed decisions in Uttarakhand
            </h2>
            <div className="mt-7 space-y-5">
              {person.bio?.map((para, i) => (
                <p key={i} className="text-[1.05rem] leading-[1.85] font-light text-[#3a3f44]">{para}</p>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-5 items-center border-t border-[#E2DDD5] pt-7">
              <SSButton to={person.key === "suhail" ? "/advisory" : "/guided"} variant="primary">
                {person.key === "suhail" ? "Start an advisory conversation" : "Begin a guided assessment"}
              </SSButton>
              {person.linkedin && (
                <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[0.82rem] text-[#1A1C1E]">
                  <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                  <span className="ss-link">LinkedIn profile</span>
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              )}
            </div>
            <p className="mt-8 text-[0.78rem] leading-relaxed text-[#8A9098]">
              This profile is part of Selling Sunsets' public authorship and entity information. <Link to="/about" className="ss-link">About Selling Sunsets</Link>.
            </p>
          </div>
        </div>
      </section>

      <ContextualCTA
        title="Considering property in Uttarakhand?"
        text="Start with the location, the land and the evidence — before the sales narrative."
        whatsappMessage={`Hi ${person.name.split(" ")[0]}, I'd like to discuss a property decision in Uttarakhand.`}
      />
    </>
  );
}
